import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../components/AdminLayout';
import RightDrawer from '../components/RightDrawer';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const STATUS_COLORS = {
  active:    { bg: '#acf44e', color: '#1a1a1a', label: 'Scheduled' },
  cancelled: { bg: '#f3f4f6', color: '#9ca3af', label: 'Cancelled' },
  completed: { bg: '#acf44e', color: '#1a1a1a', label: 'Completed' },
};

const OUTCOME_COLORS = {
  Scheduled:   { bg: '#fffbeb', color: '#7a5200' },
  Completed:   { bg: '#acf44e', color: '#1a1a1a' },
  'No Show':   { bg: '#ff082f', color: '#ffffff' },
  Rescheduled: { bg: '#eff6ff', color: '#3a6ad9' },
  Cancelled:   { bg: '#f3f4f6', color: '#374151' },
};

function Badge({ label, colorMap }) {
  const style = colorMap[label] || { bg: '#f3f4f6', color: '#374151' };
  return (
    <span style={{ display: 'inline-block', backgroundColor: style.bg, color: style.color, borderRadius: 9999, fontSize: 11, fontWeight: 600, padding: '3px 10px', whiteSpace: 'nowrap' }}>
      {label}
    </span>
  );
}

function formatDateTime(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function AdminREMeetings() {
  const token = localStorage.getItem('doorly_admin_token');
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [drawerNotes, setDrawerNotes] = useState('');
  const [drawerOutcome, setDrawerOutcome] = useState('');
  const [drawerNextAction, setDrawerNextAction] = useState('');
  const [saving, setSaving] = useState(false);
  const [hoveredRow, setHoveredRow] = useState(null);

  const fetchMeetings = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter === 'upcoming') params.set('upcoming', 'true');
      if (filter === 'past') params.set('upcoming', 'false');
      if (filter === 'cancelled') params.set('status', 'cancelled');
      const res = await fetch(`${API}/api/admin/partners/meetings?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setMeetings(data.meetings || []);
    } catch (e) { console.error('[Meetings]', e.message); }
    setLoading(false);
  }, [token, filter]);

  useEffect(() => { fetchMeetings(); }, [fetchMeetings]);

  function openDrawer(m) {
    setSelectedMeeting(m);
    setDrawerNotes(m.notes || '');
    setDrawerOutcome(m.outcome || 'Scheduled');
    setDrawerNextAction(m.next_action || '');
  }

  async function saveMeetingField(field, value) {
    if (!selectedMeeting) return;
    setSaving(true);
    try {
      await fetch(`${API}/api/admin/partners/meetings/${selectedMeeting.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ [field]: value }),
      });
      setMeetings(prev => prev.map(m => m.id === selectedMeeting.id ? { ...m, [field]: value } : m));
      setSelectedMeeting(prev => ({ ...prev, [field]: value }));
    } catch (_) {}
    setSaving(false);
  }

  const now = new Date();
  const upcoming = meetings.filter(m => m.status === 'active' && new Date(m.start_time) >= now);
  const past = meetings.filter(m => m.status === 'active' && new Date(m.start_time) < now);

  const pillStyle = (active) => ({
    padding: '6px 14px', borderRadius: 9999, fontSize: 12, fontWeight: 600, cursor: 'pointer',
    border: active ? '1px solid #3a6ad9' : '1px solid #e5e7eb',
    background: active ? '#3a6ad9' : '#fff', color: active ? '#fff' : '#374151', outline: 'none',
  });

  const saveBtn = { background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', marginTop: 6 };

  return (
    <AdminLayout>
      <div style={{ padding: '32px 32px 48px', maxWidth: 1200 }}>
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#111827', marginBottom: 4 }}>Meetings</div>
          <div style={{ fontSize: 14, color: '#6b7280' }}>Partner meetings synced from Calendly.</div>
        </div>

        {/* KPIs */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
          {[
            { label: 'Total', value: meetings.length, color: '#3a6ad9', bg: '#eff6ff', border: '#c5d3f5' },
            { label: 'Upcoming', value: upcoming.length, color: '#2d6a0a', bg: '#f4fee8', border: '#d4f99a' },
            { label: 'Past', value: past.length, color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb' },
            { label: 'Cancelled', value: meetings.filter(m => m.status === 'cancelled').length, color: '#ff082f', bg: '#fff0f3', border: '#fecdd3' },
          ].map(k => (
            <div key={k.label} style={{ background: k.bg, border: `1px solid ${k.border}`, borderRadius: 12, padding: '14px 18px' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 4 }}>{k.label}</div>
              <div style={{ fontSize: 22, fontWeight: 900, color: k.color }}>{k.value}</div>
            </div>
          ))}
        </div>

        {/* Filter pills */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
          {[['all', 'All'], ['upcoming', 'Upcoming'], ['past', 'Past'], ['cancelled', 'Cancelled']].map(([val, label]) => (
            <button key={val} style={pillStyle(filter === val)} onClick={() => setFilter(val)}>{label}</button>
          ))}
        </div>

        {/* Table */}
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #f3f4f6', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: 60, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Loading meetings...</div>
          ) : meetings.length === 0 ? (
            <div style={{ padding: 60, textAlign: 'center', color: '#9ca3af' }}>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>No meetings found</div>
              <div style={{ fontSize: 13 }}>Meetings booked through Calendly will appear here automatically.</div>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                  {['Name', 'Event', 'Date / Time', 'Duration', 'Status', 'Outcome'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '12px 16px', fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {meetings.map(m => {
                  const sc = STATUS_COLORS[m.status] || STATUS_COLORS.active;

                  return (
                    <tr key={m.id} onClick={() => openDrawer(m)}
                      onMouseEnter={() => setHoveredRow(m.id)} onMouseLeave={() => setHoveredRow(null)}
                      style={{ borderBottom: '1px solid #f9fafb', cursor: 'pointer', background: hoveredRow === m.id ? '#f9fafb' : '#fff', transition: 'background 0.1s' }}>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{m.partner_name || m.invitee_name || '—'}</div>
                        {m.partner_email && <div style={{ fontSize: 11, color: '#9ca3af' }}>{m.partner_email}</div>}
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: 13, color: '#374151' }}>{m.event_name || '—'}</td>
                      <td style={{ padding: '12px 16px', fontSize: 13, color: '#374151', whiteSpace: 'nowrap' }}>{formatDateTime(m.start_time)}</td>
                      <td style={{ padding: '12px 16px', fontSize: 13, color: '#6b7280' }}>{m.duration_minutes ? `${m.duration_minutes} min` : '—'}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 9999, background: sc.bg, color: sc.color }}>{sc.label}</span>
                      </td>
                      <td style={{ padding: '12px 16px' }}><Badge label={m.outcome || 'Scheduled'} colorMap={OUTCOME_COLORS} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Right Drawer */}
      <RightDrawer open={!!selectedMeeting} onClose={() => setSelectedMeeting(null)} title={selectedMeeting?.partner_name || selectedMeeting?.invitee_name || ''} width={440}>
        {/* ⚠️ VERTICAL ONLY — components/RightDrawer supplies the 20px horizontal
            gutter now, and `padding: 20` here would double it.
            ⚠️ AND THIS COMMENT SITS OUTSIDE THE `&& (` — a JSX comment is an
            expression, so as the first child of a parenthesised conditional it
            is an object literal where an element belongs: "Unexpected token,
            expected ','". Caught by build:ci, which is what it is for. */}
        {selectedMeeting && (
          <div style={{ padding: '20px 0', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 4 }}>Event</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{selectedMeeting.event_name}</div>
            </div>
            <div className="admin-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 4 }}>Date & Time</div>
                <div style={{ fontSize: 13, color: '#374151' }}>{formatDateTime(selectedMeeting.start_time)}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 4 }}>Duration</div>
                <div style={{ fontSize: 13, color: '#374151' }}>{selectedMeeting.duration_minutes ? `${selectedMeeting.duration_minutes} min` : '—'}</div>
              </div>
            </div>
            <div className="admin-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 4 }}>Email</div>
                <div style={{ fontSize: 13, color: '#374151' }}>{selectedMeeting.invitee_email || '—'}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 4 }}>Phone</div>
                <div style={{ fontSize: 13, color: '#374151' }}>{selectedMeeting.invitee_phone || '—'}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 4 }}>Timezone</div>
                <div style={{ fontSize: 13, color: '#374151' }}>{selectedMeeting.invitee_timezone || '—'}</div>
              </div>
            </div>
            {selectedMeeting.join_url && (
              <a href={selectedMeeting.join_url} target="_blank" rel="noopener noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#3a6ad9', color: '#fff', borderRadius: 8, fontSize: 13, fontWeight: 700, textDecoration: 'none', width: 'fit-content' }}>
                Join Meeting
              </a>
            )}
            <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 6 }}>Outcome</div>
              <select value={drawerOutcome} onChange={e => setDrawerOutcome(e.target.value)}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 13, outline: 'none' }}>
                {['Scheduled', 'Completed', 'No Show', 'Rescheduled', 'Cancelled'].map(o => <option key={o}>{o}</option>)}
              </select>
              <button disabled={saving} onClick={() => saveMeetingField('outcome', drawerOutcome)} style={saveBtn}>Save</button>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 6 }}>Notes</div>
              <textarea value={drawerNotes} onChange={e => setDrawerNotes(e.target.value)} rows={3}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 13, outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }} />
              <button disabled={saving} onClick={() => saveMeetingField('notes', drawerNotes)} style={saveBtn}>Save</button>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 6 }}>Next Action</div>
              <input value={drawerNextAction} onChange={e => setDrawerNextAction(e.target.value)}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
              <button disabled={saving} onClick={() => saveMeetingField('next_action', drawerNextAction)} style={saveBtn}>Save</button>
            </div>
            {selectedMeeting.reschedule_url && (
              <a href={selectedMeeting.reschedule_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: '#3a6ad9' }}>Reschedule on Calendly →</a>
            )}
          </div>
        )}
      </RightDrawer>
    </AdminLayout>
  );
}
